// ============================================================
// TL4K Socket.IO — URL Resolution & Configuration
// ============================================================
// Centralized URL resolution for connecting to the TL4K
// collaboration service. Works in both dev (Vite proxy) and
// production (direct HTTPS URL).
// ============================================================

export interface SocketConnectionConfig {
  /** Base URL for the collaboration service */
  url: string;
  /** Socket.IO path (required for Vite proxy in dev) */
  path: string;
  /** Whether we're in dev mode (proxy) */
  isDev: boolean;
}

/**
 * Resolve the collaboration service URL and Socket.IO path.
 *
 * Resolution order:
 *   1. `VITE_COLLABORATION_URL` env var → absolute URL, standard `/socket.io` path
 *   2. Dev fallback → `window.location.origin` with `/api-collab/socket.io` path
 *
 * IMPORTANT: When using a proxy prefix (e.g. `/api-collab`), the URL must be
 * the origin only and the prefix goes in the `path` option. Socket.IO interprets
 * the URL path as a namespace, not a proxy prefix.
 *
 * @example
 * // Production
 * VITE_COLLABORATION_URL=https://collab.example.com
 * → url: "https://collab.example.com", path: "/socket.io"
 *
 * @example
 * // Dev (Vite proxy)
 * No VITE_COLLABORATION_URL set
 * → url: "http://localhost:3000", path: "/api-collab/socket.io"
 */
export function resolveCollabUrl(
  envUrlInput?: string,
  originInput?: string,
): SocketConnectionConfig {
  const envUrl = envUrlInput ?? (typeof import.meta !== 'undefined'
    ? (import.meta as any).env?.VITE_COLLABORATION_URL
    : undefined);

  const origin = originInput ?? (typeof window !== 'undefined'
    ? window.location.origin
    : 'http://localhost:4001');

  const hasEnvUrl = typeof envUrl === 'string' && envUrl.length > 0;

  if (hasEnvUrl) {
    const trimmed = String(envUrl).replace(/\/+$/, ''); // strip trailing slash

    // Relative proxy prefix (e.g. "/api-collab"): the value is a same-origin Vite/
    // reverse-proxy path, NOT an absolute URL. socket.io's io() treats a leading-slash
    // string as a NAMESPACE, so passing it as the url breaks the handshake (wrong
    // engine path + nonexistent namespace → connect_error). Pass the origin as url and
    // put the proxy prefix in `path`, mirroring the no-env dev branch below.
    if (trimmed.startsWith('/')) {
      return {
        url: origin,
        path: `${trimmed}/socket.io`,
        isDev: true,
      };
    }

    // Absolute URL (production or explicit config)
    return {
      url: trimmed,
      path: '/socket.io',
      isDev: false,
    };
  }

  // Dev: Vite proxies /api-collab/* → localhost:4001
  return {
    url: origin,
    path: '/api-collab/socket.io',
    isDev: true,
  };
}

/**
 * Default Socket.IO client options.
 *
 * - `polling` first: avoids WebSocket upgrade issues through Vite proxy
 * - `withCredentials`: required for cookie-based sessions
 * - Reconnection: 12 attempts, 1.5s initial delay, 20s max delay
 */
export function getDefaultSocketOptions(config?: Partial<{
  /** Override transports. Default: ['polling', 'websocket'] */
  transports?: string[];
  /** Override reconnection attempts. Default: 12 */
  reconnectionAttempts?: number;
  /** Override reconnection delay. Default: 1500 */
  reconnectionDelay?: number;
}>): {
  withCredentials: boolean;
  transports: string[];
  path: string;
  reconnectionAttempts: number;
  reconnectionDelay: number;
  reconnectionDelayMax: number;
} {
  const resolved = resolveCollabUrl();

  // In dev with Vite proxy, WebSocket upgrades can be unreliable (Bun compat).
  // Use polling only in dev to avoid session corruption.
  const isDev = config?.transports
    ? false // explicit override, don't auto-switch
    : resolved.isDev;

  return {
    withCredentials: true,
    transports: config?.transports ?? (isDev ? ['polling'] : ['polling', 'websocket']),
    path: resolved.path,
    reconnectionAttempts: config?.reconnectionAttempts ?? 12,
    reconnectionDelay: config?.reconnectionDelay ?? 1500,
    reconnectionDelayMax: 20000,
  };
}
